'use client';

import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import styled from 'styled-components';
import { useCursorPagination } from '@frameio/graphql-client';
import { NetworkStatus } from '@frameio/graphql-client/utils';
import { useIsQuickSearchV2Enabled } from '@frameio/search/hooks';
import { LoadingMoreIndicator } from '@frameio/shared/components';
import {
  SEARCH_FIELD_HEIGHT,
  StyledSearchField,
} from '@frameio/shared/components/StyledSearchField';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useTrack, useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { consumeEvent, isMouseEvent } from '@frameio/utils';
import { HOTKEY } from '@frameio/utils/hooks/useHotKeys';
import type { DialogProps } from '@frameio/vapor';
import {
  Box,
  Collapse,
  CollapseContent,
  Dialog,
  Separator,
  theme,
  useOnStateChange,
  usePrevious,
  VisuallyHidden,
} from '@frameio/vapor';
import { useQuickSearchModal as useQuickSearchModalContext } from '../contexts/QuickSearchContext';
import { useGetQuickSearchResultsQuery } from '../gql/generated/GetQuickSearchResults';
import { useNavigateToResult } from '../hooks';
import { getScreenReaderText } from '../utils';
import { LIST_HEIGHT } from './constants';
import { AssetDetailPane, ProjectDetailPane } from './DetailPane';
import TEXT from './generated/TEXT/QuickSearch';
import { ResultAssetItem, ResultProjectItem } from './ResultItemCell';
import { SearchResultsList } from './SearchResultsList';

const NUMBER_SEARCH_RESULTS_TO_FETCH = 15;
const MODAL_HEIGHT = 420;
const CELL_RESULT_HEIGHT = 48;

TEXT.DEFINE({
  /** Placeholder text for the search input field */
  placeholder: 'Search for assets, folders, and projects',
  /** Message displayed when no search results are found */
  EMPTY: 'No matches found.',
  /** Message displayed while search results are loading */
  LOADING: 'Loading',
  /** Message displayed when an error occurs while fetching search results */
  ERROR: 'Failed to load results',
});

function getTimeElapsedInSeconds(startMs: number, endMs: number) {
  return ((endMs - startMs) / 1000).toPrecision(3);
}

function getResultType(resultType: 'FolderAsset' | 'Project' | unknown) {
  if (resultType === 'FolderAsset') {
    return 'Folder';
  }

  if (resultType === 'Project') {
    return 'Project';
  }

  return 'Asset';
}

function getExitMethod(methodType: 'keydown' | 'mousedown') {
  return methodType === 'keydown' ? 'esc' : 'click-away';
}

export type QuickSearchProps = {
  mockError?: Error;
  initialQuery?: string;
};

/**
 *
 * Existing QuickSearch experience
 * @TODO (SRCH-241): Deprecate/Remove once new QuickSearch experience is complete
 */
export const QuickSearch = ({ mockError, initialQuery }: QuickSearchProps) => {
  const { isEnabled: isQuickSearch2Enabled, isLoading: isQuickSearch2Loading } =
    useIsQuickSearchV2Enabled();
  const searchInitiated = useRef(false);
  const searchOpenedAt = useRef(0);
  const accountId = useActiveAccountId();
  const { toggleModal, isModalOpen } = useQuickSearchModalContext();
  const [query, setQuery] = useState<string>(initialQuery || '');
  const [searchTerm, setSearchTerm] = useState<string>(initialQuery || '');
  const [searchInitiatedAt, setSearchInitiatedAt] = useState<number>();
  const [showListAndDetail, setShowListAndDetail] = useState<boolean>(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isMountedRef = useRef(false);

  const track = useTrack();
  const trackUnsafe = useTrackUnsafe();

  const {
    data,
    previousData,
    loading,
    error: queryError,
    fetchNext,
    networkStatus,
  } = useCursorPagination(
    useGetQuickSearchResultsQuery({
      skip: searchTerm === '' || !accountId,
      variables: {
        accountId,
        page: {
          first: NUMBER_SEARCH_RESULTS_TO_FETCH,
        },
        searchTerm,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    })
  );
  const prevLoading = usePrevious(loading);
  const fetchMoreLoading = networkStatus === NetworkStatus.fetchMore;
  const pageInfo = data?.account?.quickSearchResults?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage;

  const error = queryError || mockError;
  const results = (data ?? (showListAndDetail ? previousData : undefined))
    ?.account?.quickSearchResults?.nodes;

  const isEmpty = (results?.length === 0 && !loading) || error;
  const previousResultsEmpty =
    previousData?.account?.quickSearchResults?.nodes?.length === 0;
  const hasResults =
    results && (results.length > 0 || fetchMoreLoading) && !error;

  const selectedResult = results?.[selectedIndex];
  const selectedAsset = selectedResult?.result;
  const { navigate } = useNavigateToResult(
    selectedAsset?.id ?? '',
    selectedAsset?.__typename ?? ''
  );

  const screenReaderText = getScreenReaderText({
    name: selectedAsset?.name ?? '',
    type: selectedAsset?.__typename ?? '',
  });

  const handleRequestSearch = (newQuery: string) => {
    if (isMountedRef.current) {
      if (!searchInitiated.current) {
        trackUnsafe('account-search-query-initiated', {
          search_query: newQuery,
        });
        searchInitiated.current = true;
      }

      setSearchTerm(newQuery);
      setSelectedIndex(0);
      setSearchInitiatedAt(performance.now());
    }
  };

  const handleDialogKeyDown = (e: KeyboardEvent) => {
    const goingUpList = upArrowOrShiftTab(e);
    const goingDownList = downArrowOrTab(e);

    if ((goingUpList || goingDownList) && results && results.length >= 1) {
      consumeEvent(e);
      const lastItemIndex = results.length - 1;
      if (goingUpList) {
        if (selectedIndex !== 0) {
          setSelectedIndex(selectedIndex - 1);
        }
      }

      if (goingDownList) {
        if (selectedIndex !== lastItemIndex) {
          setSelectedIndex(selectedIndex + 1);
        }
      }
    }

    if (e.key === HOTKEY.ESCAPE && !isEmptyQuery) {
      setQuery('');
    }

    if (e.key === 'Enter' && selectedAsset) {
      handleNavigate();
    }
  };

  const handleNavigate = useCallback(() => {
    trackUnsafe('account-search-result-clicked', {
      result_position: selectedIndex + 1,
      result_type: getResultType(selectedAsset?.__typename),
      search_query: searchTerm,
      time_to_click:
        searchInitiatedAt &&
        getTimeElapsedInSeconds(searchInitiatedAt, performance.now()),
      result_source: 'Standard',
    });
    navigate();
  }, [
    navigate,
    searchInitiatedAt,
    searchTerm,
    selectedAsset?.__typename,
    selectedIndex,
    trackUnsafe,
  ]);

  const isEmptyQuery = query === '';

  type OnRequestCloseParams = Parameters<
    NonNullable<DialogProps['onRequestClose']>
  >;

  const handleRequestClose = useCallback(
    (e: OnRequestCloseParams[0]) => {
      if (!e) return;

      if (query && !isMouseEvent(e)) {
        setQuery('');
      }
      if (isEmptyQuery || isMouseEvent(e)) {
        trackUnsafe('account-search-exited', {
          exit_method: getExitMethod(e.type as 'keydown' | 'mousedown'),
          last_search_query: searchTerm,
          time_spent_on_search: getTimeElapsedInSeconds(
            searchOpenedAt.current,
            performance.now()
          ),
        });

        toggleModal();
      }
    },
    [isEmptyQuery, toggleModal, setQuery, query, searchTerm, trackUnsafe]
  );

  const footerProps = useMemo(
    () => ({
      loading: fetchMoreLoading,
      height: CELL_RESULT_HEIGHT,
    }),
    [fetchMoreLoading]
  );

  const onBoundary = useCallback(
    (boundary: any) => {
      if (boundary === 'bottom' && hasNextPage) {
        fetchNext({
          count: NUMBER_SEARCH_RESULTS_TO_FETCH,
          pageInfo,
        });
      }
    },
    [fetchNext, pageInfo, hasNextPage]
  );

  const handleOnMouseDown = useCallback(() => {
    if (isModalOpen) {
      setTimeout(() => searchInputRef?.current?.focus(), 0);
    }
  }, [isModalOpen]);

  const onExited = useCallback(() => {
    setQuery('');
    setSearchTerm('');
    setSelectedIndex(0);
    searchInitiated.current = false;
  }, []);

  const onEntered = useCallback(() => {
    searchOpenedAt.current = performance.now();
  }, []);

  const handleResultClick = (index: number) => {
    setSelectedIndex(index);
  };

  // prevent showing the modal contents until a query has been entered and
  // initial results have been returned (at least once). Hide again when
  // query is cleared.
  useEffect(() => {
    isMountedRef.current = true;

    if (
      !showListAndDetail &&
      !isEmptyQuery &&
      results !== undefined &&
      !error
    ) {
      setShowListAndDetail(true);
    } else if (isEmptyQuery) {
      setShowListAndDetail(false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [showListAndDetail, isEmptyQuery, results, error]);

  useEffect(
    function trackNoResults() {
      if (isEmpty) {
        trackUnsafe('account-search-no-results', {
          search_query: searchTerm,
          web_source: 'Modal',
        });
      }
    },
    [isEmpty, searchTerm, trackUnsafe]
  );

  const latestResultsCount =
    data?.account?.quickSearchResults?.nodes?.length ?? 0;
  const hasNewResults =
    prevLoading && !loading && latestResultsCount > 0 && !error;

  useEffect(
    function trackResultsDisplayed() {
      // Guard needed: V1 and V2 components are both mounted in layouts simultaneously.
      // V2 handles its own tracking via useQuickSearchResultsAnalytics hook.
      if (isQuickSearch2Enabled || isQuickSearch2Loading) return;
      if (hasNewResults) {
        track('account-search-results-displayed', {
          is_standard_results_displayed: latestResultsCount > 0,
          is_media_intelligence_results_displayed: false,
          search_query: searchTerm,
        });
      }
    },
    [
      hasNewResults,
      track,
      latestResultsCount,
      searchTerm,
      searchInitiatedAt,
      isQuickSearch2Enabled,
      isQuickSearch2Loading,
    ]
  );

  const onRouteChange = () => {
    if (isModalOpen) {
      toggleModal();
    }
  };

  const pathName = usePathname();
  useOnStateChange(pathName, onRouteChange);

  if (isQuickSearch2Enabled) return null;

  return (
    <Dialog
      aria-labelledby="QuickSearch-modal"
      data-testid="quick-search-modal"
      hasOutermostTheme={true}
      initialFocusRef={searchInputRef}
      isShowing={isModalOpen}
      onEntered={onEntered}
      onExited={onExited}
      onKeyDown={handleDialogKeyDown}
      onMouseDown={handleOnMouseDown}
      onRequestClose={handleRequestClose}
      position="top"
      size="large"
    >
      <Wrapper>
        <StyledSearchField
          ref={searchInputRef}
          autoCapitalize="off"
          autoCorrect="off"
          data-testid="quick-search-input"
          debounce={200}
          enableClearButton={false}
          error={error && TEXT.ERROR}
          isSearching={loading && !fetchMoreLoading}
          muted={true}
          onQueryChange={setQuery}
          onRequestSearch={handleRequestSearch}
          placeholder={TEXT.placeholder}
          query={query}
        />
        {error && (
          <Box
            data-testid="quick-search-error-container"
            position="absolute"
            right="162px"
            top="20px"
          >
            <VisuallyHidden>{TEXT.ERROR}</VisuallyHidden>
          </Box>
        )}
        <SeparatorContainer
          $show={!error && (!!showListAndDetail || !!isEmpty)}
        >
          <Separator />
        </SeparatorContainer>
        <Collapse isShowing={showListAndDetail}>
          <CollapseContent>
            <InnerWrapper>
              <AnimatePresence>
                <ListAndDetailContainer
                  key={'quick-search-results-container'}
                  $height={hasResults ? LIST_HEIGHT : 0}
                  {...crossfade}
                  data-testid="list-and-detail-container"
                >
                  {hasResults && (
                    <>
                      <VisuallyHidden>
                        <div role="status">{screenReaderText}</div>
                      </VisuallyHidden>
                      <SearchResultsList
                        footer={<LoadingMoreIndicator {...footerProps} />}
                        itemHeight={CELL_RESULT_HEIGHT}
                        list={results}
                        onBoundary={onBoundary}
                        scrollBoundaryMargin={MODAL_HEIGHT / 2}
                        scrollToIndex={selectedIndex}
                      >
                        {(result, index) => {
                          const itemProps = {
                            isSelected: index === selectedIndex,
                            onClick: () => handleResultClick(index),
                            onDoubleClick: handleNavigate,
                            resultId: result.id,
                          };

                          return (
                            <ListItem key={result.id}>
                              {result.__typename === 'Project' ? (
                                <ResultProjectItem {...itemProps} />
                              ) : (
                                <ResultAssetItem {...itemProps} />
                              )}
                            </ListItem>
                          );
                        }}
                      </SearchResultsList>
                    </>
                  )}
                  {hasResults && !error && (
                    <>
                      <motion.div {...crossFadeWithDelay}>
                        <VerticalSeparator height={'100%'} vertical={true} />
                      </motion.div>
                      {selectedAsset?.__typename === 'Project' ? (
                        <ProjectDetailPane resultId={selectedAsset?.id ?? ''} />
                      ) : (
                        <AssetDetailPane resultId={selectedAsset?.id ?? ''} />
                      )}
                    </>
                  )}
                </ListAndDetailContainer>
                {(isEmpty || (loading && previousResultsEmpty)) && !error && (
                  <CrossfadeContainer role="status" {...crossfade}>
                    <EmptyText>{TEXT.EMPTY}</EmptyText>
                  </CrossfadeContainer>
                )}
              </AnimatePresence>
            </InnerWrapper>
          </CollapseContent>
        </Collapse>
      </Wrapper>
    </Dialog>
  );
};

function downArrowOrTab(e: KeyboardEvent) {
  return e.key === HOTKEY.ARROW_DOWN || (e.key === HOTKEY.TAB && !e.shiftKey);
}

function upArrowOrShiftTab(e: KeyboardEvent) {
  return e.key === HOTKEY.ARROW_UP || (e.key === HOTKEY.TAB && e.shiftKey);
}

const Wrapper = styled.div`
  border-radius: ${theme.radii[8]};
`;

const InnerWrapper = styled.div`
  height: calc(${MODAL_HEIGHT}px - ${SEARCH_FIELD_HEIGHT}px);
`;

const ListAndDetailContainer = styled(motion.div)<{ $height: number }>`
  display: flex;
  height: ${({ $height }) => $height}px;
`;

const ListItem = styled(motion.li)`
  list-style-type: none;
`;

const EmptyText = styled.p`
  color: ${theme.colors.text.tertiary};
  font-size: ${theme.fontSizes[95]};
  line-height: ${theme.lineHeights[100]};
  opacity: 0.65;
`;

const CrossfadeContainer = styled(motion.div)`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  z-index: ${theme.zIndices.modal};
`;

const crossfade = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
  },
  transition: {
    duration: 0.3,
    ease: theme.transitions.curves.primary,
  },
};

const crossFadeWithDelay = {
  ...crossfade,
  transition: {
    ...crossfade.transition,
    delay: 0.2,
  },
};

const SeparatorContainer = styled.div<{ $show: boolean }>`
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: opacity 0.2s ${theme.transitions.eases.primary};
  transition-delay: ${({ $show }) => !$show && '0.1s'};
`;

const VerticalSeparator = styled(Separator)`
  display: none;

  ${theme.mediaQueries.tablet} {
    display: block;
  }
`;
